import { termOfValidityRender } from 'views/Marketing/components/PolicyTable';
import { getSlateExecuteContext, useShippingStatusLabel } from 'views/Aide/Scenario/PolicyForm';
import { useGetResultList } from 'views/Aide/Scenario/PolicyForm/TranslateTool/Result';

import { LogItemProps } from '../LogItemLayout';
import { useGetCheckDisplay, useGetSwitchDisplay, useScenarioLabel } from '../useUtils';
import { useMemoizedFn } from 'ahooks';
import { isBoolean, isString, toNumber } from 'lodash-es';
import { Text } from '../LogItemTask/useDetail';
import TransComponent from 'components/TransComponent';
import { T, useTranslate } from '@tolgee/react';

// 枚举渲染 太多值了 直接用 any
const getEnumText = (data: any, enumMap: any) => {
  return data?.map?.((v: any) => <div>{enumMap?.[v]}</div>);
};

export const useDetail = (
  data: LogItemProps['data'],
  platform: General.PlatformUpperCase,
  region: General.UpperCaseRegion,
) => {
  const { t } = useTranslate();
  const { operateType, changeField, changeDetail, scenarioType } = data;
  const statusLabelMap = useShippingStatusLabel();
  const scenarioLabel = useScenarioLabel(scenarioType);
  const getSwitchDisplay = useGetSwitchDisplay();
  const getCheckDisplay = useGetCheckDisplay();
  const getResultList = useGetResultList({
    platform,
    region,
  });

  const getExecuteContext = useMemoizedFn((executeContext: Marketing.ExecuteContext) => {
    const resultList = getResultList(
      getSlateExecuteContext(executeContext),
      undefined,
      executeContext?.translatedContent !== '#mustache#',
    );
    const result = resultList?.filter((r) => r.type === 'reply');
    return result?.map((r) => r.content ?? '');
  });

  if (operateType !== 'UPDATE_POLICY') return null;

  if (changeField === 'POLICY_EXECUTE_CONTEXT') {
    // @ts-ignore
    const before = changeDetail?.before?.map?.((executeContext) =>
      getExecuteContext(executeContext),
    );
    // @ts-ignore
    const after = changeDetail?.after?.map?.((executeContext) => getExecuteContext(executeContext));

    return {
      // @ts-ignore
      before: before?.map((text) => <div>{text}</div>),
      // @ts-ignore
      after: after?.map((text) => <div>{text}</div>),
    };
  }

  if (changeField === 'POLICY_TERM_OF_VALIDITY') {
    // 策略有效期
    return {
      // @ts-expect-error
      before: termOfValidityRender(changeDetail?.before),
      // @ts-expect-error
      after: termOfValidityRender(changeDetail?.after),
    };
  }

  if (changeField === 'POLICY_LIMIT_ITEM_TYPE') {
    // 策略指定商品类型
    const labelMapper = {
      ALL: t('aide.全部商品'),
      SPECIFIC: t('aide.指定商品'),
      PRE_ORDER: t('aide.预售'),
      IN_STOCK: t('aide.现货'),
    };
    return {
      // @ts-ignore
      before: labelMapper[changeDetail?.before],
      // @ts-ignore
      after: labelMapper[changeDetail?.after],
    };
  }

  if (changeField === 'POLICY_CANCEL_TRADE_SHIPPING_STATUS') {
    // 取消订单出货进度
    return {
      // @ts-ignore
      before: getEnumText(changeDetail?.before, statusLabelMap),
      // @ts-ignore
      after: getEnumText(changeDetail?.after, statusLabelMap),
    };
  }

  if (changeField === 'POLICY_CANCEL_TRADE_AUTO_CANCEL' || changeField === 'POLICY_ENABLE') {
    // 开关
    // 取消订单自动同意
    return {
      before: getSwitchDisplay(changeDetail?.before),
      after: getSwitchDisplay(changeDetail?.after),
    };
  }

  if (changeField === 'POLICY_CANCEL_TRADE_SEND_POINT') {
    // 取消订单发送时机
    const labelMapper = {
      IN_CANCEL: t('aide.买家申请取消订单，待卖家处理'),
      CANCELLED: t('aide.买家申请取消订单，申请已通过'),
    };
    return {
      before: getEnumText(changeDetail?.before, labelMapper),
      after: getEnumText(changeDetail?.after, labelMapper),
    };
  }

  if (changeField === 'POLICY_CANCEL_TRADE_EXECUTE_CONTEXT') {
    // 取消订单发送话术
    return {
      // @ts-ignore
      before: getExecuteContext(changeDetail?.before?.executeContext),
      // @ts-ignore
      after: getExecuteContext(changeDetail?.after?.executeContext),
    };
  }

  if (changeField === 'POLICY_AUTO_REVIEW_CONTENT') {
    // 自动回评-评价内容类型
    const labelMapper = {
      ANY: t('review.带文字/图像/视频'),
      EMPTY: t('review.无内容'),
    };
    return {
      // @ts-ignore
      before: changeDetail?.before?.map((v) => labelMapper[v]).join(', '),
      // @ts-ignore
      after: changeDetail?.after?.map((v) => labelMapper[v]).join(', '),
    };
  }

  if (changeField === 'POLICY_RETURN_TRADE_RETURN_TYPE') {
    // 退单类型
    const labelMapper = {
      ONLY_REFUND: t('aide.仅退款'),
      RETURN_REFUND: t('aide.退货退款'),
    };
    return {
      // @ts-ignore
      before: getEnumText(changeDetail?.before, labelMapper),
      // @ts-ignore
      after: getEnumText(changeDetail?.after, labelMapper),
    };
  }

  if (changeField === 'POLICY_AUTO_REVIEW_STARTS') {
    // 自动回评星级	[3,4,5]
    return {
      // @ts-ignore
      before: changeDetail?.before?.map?.((x) => (
        <div>{t('review.x星', { count: toNumber(x) })}</div>
      )),
      // @ts-ignore
      after: changeDetail?.after?.map?.((x) => (
        <div>{t('review.x星', { count: toNumber(x) })}</div>
      )),
    };
  }

  if (
    // 策略名称
    changeField === 'POLICY_NAME' ||
    // 自动回评延迟天数
    changeField === 'POLICY_AUTO_REVIEW_DELAY_DAY' ||
    // 延迟发货天数
    changeField === 'POLICY_DELAY_SHIP_DELAY_DAY' ||
    // 取消订单生效时限
    changeField === 'POLICY_CANCEL_TRADE_AUTO_CANCEL_LIMIT_HOUR' ||
    changeField === 'ROUND_EXECUTE_TIME'
  ) {
    return {
      before: changeDetail?.before,
      after: changeDetail?.after,
    };
  }

  if (changeField === 'ROUND_EXECUTE_TIME_UNIT' && changeDetail?.before && changeDetail?.after) {
    const timeMapper = {
      MINUTES: t('marketing.分钟'),
      HOURS: t('marketing.小时'),
    };
    return {
      // @ts-ignore
      before: timeMapper[changeDetail.before],
      // @ts-ignore
      after: timeMapper[changeDetail.after],
    };
  }

  if (
    (changeField === 'POLICY_PRAISE_TRIGGER_POINT' ||
      changeField === 'TASK_PRAISE_TRIGGER_POINT') &&
    changeDetail?.before &&
    changeDetail?.after &&
    isString(changeDetail?.before) &&
    isString(changeDetail?.after)
  ) {
    // 催好评发送时间节点
    const praiseTriggerLabel = {
      SIGNED: t('marketing.买家签收'),
      CONFIRMED: t('marketing.买家确认收货'),
    };
    return {
      // @ts-ignore
      before: praiseTriggerLabel[changeDetail?.before],
      // @ts-ignore
      after: praiseTriggerLabel[changeDetail?.after],
    };
  }

  if (
    changeField === 'TASK_FILTER_HAS_CONVERSATION' ||
    changeField === 'POLICY_FILTER_HAS_CONVERSATION'
  ) {
    // 过滤下单后在该店铺有过咨询的买家
    const text = t('aide.过滤下单后在该店铺有过咨询的买家');
    return {
      before: getCheckDisplay(changeDetail?.before, text),
      after: getCheckDisplay(changeDetail?.after, text),
    };
  }

  if (
    changeField === 'TASK_FILTER_AFTER_SALE_ENABLE' ||
    changeField === 'POLICY_FILTER_AFTER_SALE_ENABLE'
  ) {
    // 售后买家开关
    const text = t('aide.售后买家');
    return {
      before: getCheckDisplay(changeDetail?.before, text),
      after: getCheckDisplay(changeDetail?.after, text),
    };
  }

  if (
    changeField === 'TASK_FILTER_AFTER_SALE_CONFIRM_DAYS' ||
    changeField === 'POLICY_FILTER_AFTER_SALE_CONFIRM_DAYS'
  ) {
    // 售后买家-确认收天数
    return {
      before: (
        <T
          keyName="marketing.买家确认收货x天内与客服对话，视为咨询售后问题，将不会触发action"
          params={{ action: scenarioLabel, x: <Text text={changeDetail?.before} /> }}
        />
      ),
      after: (
        <T
          keyName="marketing.买家确认收货x天内与客服对话，视为咨询售后问题，将不会触发action"
          params={{ action: scenarioLabel, x: <Text text={changeDetail?.after} /> }}
        />
      ),
    };
  }

  if (
    (changeField === 'TASK_FILTER_AFTER_SALE_OUT_OF_ORDER_ITEM' ||
      changeField === 'POLICY_FILTER_AFTER_SALE_OUT_OF_ORDER_ITEM') &&
    isBoolean(changeDetail?.before) &&
    isBoolean(changeDetail?.before)
  ) {
    // 买家咨询订单外商品开关
    const text = t('marketing.在上述期间，若买家咨询订单外商品，仍然可以action', {
      action: scenarioLabel,
    }) as string;
    return {
      before: getCheckDisplay(changeDetail?.before, text),
      after: getCheckDisplay(changeDetail?.after, text),
    };
  }

  if (changeField === 'POLICY_FILTER_RULE_FILTER_WAIT_PROCESS_CONVERSATION') {
    const text = t('aide.过滤下发时处于待处理状态的买家');
    return {
      before: getCheckDisplay(changeDetail?.before, text),
      after: getCheckDisplay(changeDetail?.after, text),
    };
  }

  if (changeField === 'POLICY_FILTER_RULE_FILTER_CONSULT') {
    const text = t('aide.过滤近x天咨询过的买家').replace('<x></x>', 'x');
    return {
      before: getCheckDisplay(changeDetail?.before, text),
      after: getCheckDisplay(changeDetail?.after, text),
    };
  }

  if (changeField === 'POLICY_FILTER_RULE_FILTER_CONSULT_DAYS') {
    return {
      before: (
        <T
          keyName="aide.过滤近x天咨询过的买家"
          params={{
            x: <TransComponent content={<>{changeDetail?.before}</>} />,
          }}
        />
      ),
      after: (
        <T
          keyName="aide.过滤近x天咨询过的买家"
          params={{
            x: <TransComponent content={<>{changeDetail?.after}</>} />,
          }}
        />
      ),
    };
  }

  if (changeField === 'POLICY_FILTER_RULE_FILTER_CONSULT_AFTER_CREATE_ORDER') {
    const text = t('aide.过滤下单后在该店铺有过咨询的买家');
    return {
      before: getCheckDisplay(changeDetail?.before, text),
      after: getCheckDisplay(changeDetail?.after, text),
    };
  }

  if (changeField === 'POLICY_FILTER_RULE_FILTER_NEGATIVE_BUYER') {
    const text = t('ai_task_form_filter_negative_buyer');
    return {
      before: getCheckDisplay(changeDetail?.before, text),
      after: getCheckDisplay(changeDetail?.after, text),
    };
  }

  return null;
};
